import * as React from "react";
import classNames from "classnames";

import { SizeTile } from "@lululemon/ecom-pattern-library-size-tile";
import { ButtonTile } from "@lululemon/ecom-pattern-library-button-tile";
import { ButtonTileGroup } from "@lululemon/ecom-pattern-library-button-tile-group";

import PurchaseAttributeCarouselCounter from "./components/purchase-attribute-carousel-counter/purchase-attribute-carousel-counter";
import ShopAllSizeLink from "./components/shop-all-size-link/shop-all-size-link";
import { hybridSizingCategoriesMap } from "./components/hybrid-sizing/size-selector-equivalencies";
import { isHybridSizing } from "./components/hybrid-sizing/utils";

import styles from "./styles/size-selector.module.scss";

/**
 * This is a temporary hack to work around the fact that the pattern library's `ButtonTileGroup`
 * component clones its children and passes them a `ref` prop without handling the case where a ref
 * is already being passed in.
 *
 * @param {Object} props - the component props
 * @param {Function} props.renderTile - the render function to render the ButtonTile
 * @param {Array<Function|{ current: any }>} props.refsToSet - the refs to set on the tile
 * @param {Object} props.forwardProps - the additional props to forward to the rendered ButtonTile
 * @param {React.ReactNode} props.children - the children to render
 * @param {React.Ref} ref - the ref to forward to the rendered ButtonTile
 *
 * @returns {React.ReactElement}
 */
const ButtonTileRefWrapper = React.forwardRef(
  ({ renderTile, refsToSet, ...forwardProps }, ref) => {
    const refs = [...(refsToSet || []), ref].filter(Boolean);

    return renderTile({
      ...forwardProps,
      ref: (node) => {
        refs.forEach((r) => {
          if (typeof r === "function") {
            r(node);
          } else {
            r.current = node;
          }
        });
      }
    });
  }
);

ButtonTileRefWrapper.displayName = "ButtonTileRefWrapper";

/**
 * SizeSelector component for selecting sizes of a product.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.noPaddingTop - Flag indicating if the component wrapper should have no top padding.
 * @param {string} props.className - Additional class name to be applied to the ButtonTileGroup sub-component.
 * @param {string} props.rangeLabel - The range label for the size selector.
 * @param {{ label: string, value: string }} props.primaryLabel - The primary label for the size selector.
 * @param {string} props.activeSize - The currently selected size.
 * @param {string[]} props.allSizes - The list of all available sizes.
 * @param {boolean} props.isMobile - Flag indicating if the view is mobile.
 * @param {Function} props.onSelectCallback - Callback function when a size is selected.
 * @param {string[]} props.availableSizes - The list of available sizes (in stock).
 * @param {boolean} props.hasInseams - Flag indicating if the product has inseams.
 * @param {Object} props.sizeSelectorButtonTileRef - Ref for the size selector active button tile.
 * @param {string} props.category - The category of the product.
 * @param {string} props.displayName - The display name of the product.
 * @param {boolean} props.pdpHybridSizingABflag - Flag indicating if hybrid sizing is enabled.
 * @param {boolean} props.isFrCA - Flag indicating if the view is for French Canada.
 * @param {boolean} props.shouldShowShopAllSizeLink - Flag indicating if the "Shop All Size" link should be shown.
 * @param {string} props.oosSizeUrl - The URL for the "Shop All Size" link.
 * @param {Function} props.getResizeSubscriber - Function to get the resize subscriber.
 * @param {string} props.categoryName - The name of the product category.
 * @param {Object[]} props.breadCrumbs - The breadcrumb navigation.
 * @param {Function} props.trackSizeTileClick - Function to track size tile clicks.
 * @param {React.ReactNode} props.children - The children to render inside the component.
 * @param {React.ReactNode} props.hint - The hint to display in the size selector.
 * @param {Object} props.otherProps - Additional props for the component.
 * @param {React.ReactNode} props.rightArrowIcon - The right arrow icon to display in the carousel-counter sub-component.
 *
 * @returns {React.ReactElement}
 */
const SizeSelector = ({
  noPaddingTop = false,
  className = "",
  rangeLabel = "",
  primaryLabel = { label: "", value: "" },
  activeSize = "",
  allSizes = [],
  isMobile = false,
  onSelectCallback = () => {},
  availableSizes = [],
  hasInseams = false,
  sizeSelectorButtonTileRef,
  children = null,
  hint = null,
  category = "",
  displayName = "",
  pdpHybridSizingABflag = false,
  isFrCA = false,
  shouldShowShopAllSizeLink = false,
  oosSizeUrl,
  getResizeSubscriber,
  categoryName,
  breadCrumbs = [],
  trackSizeTileClick,
  rightArrowIcon,
  ...otherProps
}) => {
  const carouselCounterId = `pdp-size-selector-button-tile-group-${React.useId()}`;
  const isHybridSizingEnabled =
    isHybridSizing(category, displayName) && pdpHybridSizingABflag && !isFrCA;

  const [isHeadingCounterVisible, setIsHeadingCounterVisible] =
    React.useState(false);
  const tabScrollerRef = React.useRef(null);
  const [isSizeTileWide, setIsSizeTileWide] = React.useState(false);
  const sizeTileRefs = React.useRef([]);

  const selectedSizeIndex = allSizes?.indexOf(activeSize);

  React.useEffect(() => {
    if (allSizes?.length && sizeTileRefs?.current?.length) {
      sizeTileRefs.current = sizeTileRefs.current.slice(0, allSizes.length);

      const checkTileWidth = () => {
        const WIDE_TILE_WIDTH = 55;
        const isWide = sizeTileRefs.current.some(
          (ref) => ref && ref.offsetWidth > WIDE_TILE_WIDTH
        );
        setIsSizeTileWide(isWide);
      };

      checkTileWidth();
    }
  }, [allSizes]);

  const handleButtonTileSelect = (index = 0) => {
    if (index !== selectedSizeIndex) {
      const newSelectedSize = allSizes[index];
      trackSizeTileClick?.({
        text: newSelectedSize,
        position: index + 1
      });
      onSelectCallback(newSelectedSize);
    }
  };

  const setSizeCounterVisibility = React.useCallback(() => {
    if (tabScrollerRef?.current) {
      const { scrollWidth, clientWidth } = tabScrollerRef.current;
      if (scrollWidth && clientWidth) {
        setIsHeadingCounterVisible(scrollWidth > clientWidth);
      }
    }
  }, []);

  const handleResize = React.useCallback(() => {
    if (isMobile) {
      setSizeCounterVisibility();
    }
  }, [isMobile, setSizeCounterVisibility]);

  React.useEffect(() => {
    getResizeSubscriber?.(handleResize);
    setSizeCounterVisibility();
  }, [allSizes, handleResize, setSizeCounterVisibility]);

  const renderTileText = React.useCallback(
    (size, index) => {
      const isSelected = index === selectedSizeIndex;
      return (
        <div
          ref={(el) => (sizeTileRefs.current[index] = el)}
          className={styles.tileSize}
        >
          <span
            className={`${styles.size} lll-text-body-1 lll-font-weight-semibold`}
          >
            {size}
          </span>
          {hybridSizingCategoriesMap?.[category]?.[size] && (
            <span
              className={classNames(styles.equivalency, "lll-text-body-3", {
                [styles.isNotSelected]: !isSelected
              })}
            >
              {hybridSizingCategoriesMap?.[category]?.[size]}
            </span>
          )}
        </div>
      );
    },
    [category, selectedSizeIndex]
  );

  const renderSizeTile = React.useCallback(
    (size, index) => {
      if (!isHybridSizingEnabled) {
        return <SizeTile text={size} />;
      }

      return (
        <SizeTile
          text={renderTileText(size, index)}
          className={
            isSizeTileWide ? styles.sizeTileWide : styles.sizeTileNarrow
          }
        />
      );
    },
    [isHybridSizingEnabled, renderTileText, isSizeTileWide]
  );

  const sizeTileRefIndex = allSizes?.[selectedSizeIndex]
    ? selectedSizeIndex
    : 0;
  const renderButtonTile = React.useCallback(
    (size, index) => {
      // This ref is used for a11y behavior to set the focus on the selected size tile or the first
      // size tile if none is selected when the PDP's `scrollToSelectSize` function is called.
      const shouldSetSizeTileRef = index === sizeTileRefIndex;

      return (
        <ButtonTileRefWrapper
          key={`size-buttontile-${size}`}
          refsToSet={shouldSetSizeTileRef ? [sizeSelectorButtonTileRef] : []}
          renderTile={(forwardedProps) => (
            <ButtonTile
              {...forwardedProps}
              className={classNames(
                {
                  [styles.sizeSelectorButtonTile]: !isMobile
                },
                "OneLinkNoTx"
              )}
              isDisabled={!availableSizes.includes(size)}
            >
              {renderSizeTile(size, index)}
            </ButtonTile>
          )}
        />
      );
    },
    [
      sizeTileRefIndex,
      sizeSelectorButtonTileRef,
      isMobile,
      availableSizes,
      renderSizeTile
    ]
  );

  if (!allSizes?.length) return null;

  return (
    <div
      className={classNames(styles.sizeSelector, {
        [styles.sizeSelectorNoPadding]: noPaddingTop
      })}
      {...otherProps}
    >
      <PurchaseAttributeCarouselCounter
        headingRangeTitle={rangeLabel}
        id={carouselCounterId}
        isHeadingCountVisible={isHeadingCounterVisible}
        primaryLabel={primaryLabel}
        rangeArray={allSizes}
        hasInseams={hasInseams}
        isHybridSizingEnabled={isHybridSizingEnabled}
        category={category}
        icon={rightArrowIcon}
      >
        {children}
      </PurchaseAttributeCarouselCounter>
      <ButtonTileGroup
        className={classNames(styles.buttonTileGroup, "OneLinkNoTx", className)}
        ariaLabelledby={carouselCounterId}
        onSelectCallback={handleButtonTileSelect}
        selectedIndex={selectedSizeIndex}
      >
        {allSizes.map(renderButtonTile)}
      </ButtonTileGroup>
      {hint}
      {shouldShowShopAllSizeLink && (
        <ShopAllSizeLink
          oosSizeUrl={oosSizeUrl}
          activeSize={activeSize}
          categoryName={categoryName}
          breadCrumbs={breadCrumbs}
        />
      )}
    </div>
  );
};

export default SizeSelector;
